import CopyBtn from "@/components/copy-btn";
import { useOpenReturnWidget } from "@/pages/(app)/embed/use-open-return-widget";
import { useSuperAdminReturnableOrder } from "@babackapp/private-api-client/client/use-super-admin-shops";
import type { SuperAdminReturnableOrderDto } from "@babackapp/private-api-client/generated/schema";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { ExternalLink, Loader2, SparklesIcon } from "lucide-react";
import { useState } from "react";

const FIELDS = [
  { key: "orderNumber", label: "Order number" },
  { key: "orderId", label: "Order ID" },
  { key: "customerEmail", label: "Email" },
  { key: "zipCode", label: "Code postal" },
] as const satisfies readonly {
  key: keyof SuperAdminReturnableOrderDto;
  label: string;
}[];

/** Opens the shop's return portal on its storefront (Shopify app proxy). */
function ReturnPortalLink({
  storefrontUrl,
  label,
}: {
  storefrontUrl: string;
  label?: string;
}) {
  return (
    <a
      href={`${storefrontUrl}/a/return`}
      target="_blank"
      rel="noreferrer"
      title="Ouvrir la page retour du shop"
      className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-violet-600"
    >
      {label}
      <ExternalLink className="size-3.5" />
    </a>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {value ? (
        <CopyBtn value={value} className="text-sm font-medium">
          {value}
        </CopyBtn>
      ) : (
        <span className="text-sm text-muted-foreground">–</span>
      )}
    </div>
  );
}

function TestOrderFields({ shopId }: { shopId: string }) {
  const { data, isPending } = useSuperAdminReturnableOrder(shopId, true);

  if (isPending) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data?.orderNumber) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        Aucune commande test : ce shop n'a pas de retour récent exploitable.
      </p>
    );
  }

  return (
    <div>
      {FIELDS.map(({ key, label }) => (
        <FieldRow key={key} label={label} value={data[key] ?? null} />
      ))}
    </div>
  );
}

/**
 * Same overlay as the sidebar's demo entry, on the row's shop instead of the
 * one the staff member is currently browsing. The dialog closes first: the
 * widget renders above it, and a modal dialog would swallow its clicks.
 */
function DemoPortalButton({
  shopifyUrl,
  providerId,
  onOpen,
}: {
  shopifyUrl: string;
  providerId: string;
  onOpen: () => void;
}) {
  const openReturnWidget = useOpenReturnWidget(shopifyUrl, providerId, {
    forceNewWidget: true,
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      title="Ouvrir la démo du nouveau portail"
      onClick={() => {
        onOpen();
        void openReturnWidget();
      }}
    >
      <SparklesIcon className="size-4 text-indigo-500" />
    </Button>
  );
}

/**
 * Looks up, on demand, an order of the shop still holding a non-returned item
 * and lays out its portal credentials as copyable fields. Fetching is lazy,
 * and starts only once the dialog opens, because it costs one query per shop.
 */
function TestOrderDialog({
  shopId,
  shopifyUrl,
  providerId,
}: {
  shopId: string;
  shopifyUrl: string;
  providerId: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Commande test
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start justify-between gap-2">
            <DialogTitle>Commande test</DialogTitle>
            <DemoPortalButton
              shopifyUrl={shopifyUrl}
              providerId={providerId}
              onOpen={() => setIsOpen(false)}
            />
          </div>
          <DialogDescription>
            Une commande issue d'un retour récent du shop, gardant un article
            encore retournable, à saisir dans le formulaire du portail.
          </DialogDescription>
        </DialogHeader>
        {isOpen && <TestOrderFields shopId={shopId} />}
      </DialogContent>
    </Dialog>
  );
}

export function CustomerPortalTestOrder({
  shopId,
  shopifyUrl,
  providerId,
  storefrontUrl,
}: {
  shopId: string;
  shopifyUrl: string;
  providerId: string;
  storefrontUrl: string | null;
}) {
  return (
    <div className="flex items-center gap-2">
      <TestOrderDialog
        shopId={shopId}
        shopifyUrl={shopifyUrl}
        providerId={providerId}
      />
      {storefrontUrl && (
        <ReturnPortalLink storefrontUrl={storefrontUrl} label="Portail" />
      )}
    </div>
  );
}
